import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  CheckCircle, 
  AlertCircle,
  User,
  Building,
  MessageSquare,
  FileText
} from 'lucide-react';
import { ContactFormData } from '../types';
import apiService from '../services/api';
import SEO from '../components/SEO';
import { companyConfig } from '../config/company';

const schema: yup.ObjectSchema<ContactFormData> = yup.object({
  name: yup.string().required('El nombre es requerido').min(2, 'El nombre debe tener al menos 2 caracteres'),
  company: yup.string().optional(),
  phone: yup.string().required('El teléfono es requerido').min(10, 'El teléfono debe tener al menos 10 dígitos'),
  email: yup.string().required('El email es requerido').email('El email no es válido'),
  subject: yup.string().oneOf(['cotizacion', 'informacion_general']).required('El asunto es requerido'),
  message: yup.string().required('El mensaje es requerido').min(10, 'El mensaje debe tener al menos 10 caracteres')
});

const Contact: React.FC = () => {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const mapEmbedUrl =
    process.env.REACT_APP_MAPS_EMBED_URL ||
    `https://www.google.com/maps?q=${encodeURIComponent(companyConfig.address)}&output=embed`;

  const searchParams = new URLSearchParams(location.search);
  const requestedProduct = searchParams.get('product') || searchParams.get('producto') || '';
  const requestedSubject = searchParams.get('subject') || searchParams.get('asunto') || '';
  const defaultSubject = requestedSubject === 'cotizacion' ? 'cotizacion' : '';
  const defaultMessage = requestedProduct
    ? `Hola, quiero solicitar cotización para el producto: ${requestedProduct}, por la cantidad de X bolsas.`
    : '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      subject: defaultSubject as ContactFormData['subject'],
      message: defaultMessage
    }
  });

  useEffect(() => {
    if (defaultSubject) {
      setValue('subject', 'cotizacion');
    }
    if (defaultMessage) {
      setValue('message', defaultMessage);
    }
  }, [defaultSubject, defaultMessage, setValue]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await apiService.sendContactMessage(data);
      apiService.trackAnalytics('contact_submitted', '/contacto').catch(() => undefined);
      setSubmitStatus('success');
      setSubmitMessage('¡Mensaje enviado correctamente! Te contactaremos pronto.');
      reset();
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
      console.error('Error sending contact message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="h-6 w-6 text-primary-600" />,
      title: "Dirección",
      content: "Santiago de Liniers 1239, Rivadavia, San Juan, Argentina"
    },
    {
      icon: <Phone className="h-6 w-6 text-primary-600" />,
      title: "Teléfono",
      content: "(264) 411-4422",
      link: "tel:+542644114422"
    },
    {
      icon: <Mail className="h-6 w-6 text-primary-600" />,
      title: "Email",
      content: "plasticospolcen@gmail.com",
      link: "mailto:plasticospolcen@gmail.com"
    },
    {
      icon: <Clock className="h-6 w-6 text-primary-600" />,
      title: "Horarios",
      content: "Lunes a Viernes: 08:00 - 17:00"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Contacto"
        description="Contacta con Plásticos Polcen. Solicita cotizaciones, información sobre productos o asesoramiento técnico. Teléfono: (264) 411-4422, Email: plasticospolcen@gmail.com"
        keywords="contacto, cotización, teléfono, email, asesoramiento técnico, información productos, San Juan Argentina"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contactanos
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Estamos aquí para ayudarte con tu proyecto
            </p>
            <p className="text-lg mb-10 text-primary-200 max-w-3xl mx-auto">
              ¿Necesitas una cotización personalizada? ¿Tienes alguna consulta sobre nuestros productos? 
              Contáctanos y te responderemos a la brevedad.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Formulario de Contacto */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Envíanos un Mensaje
            </h2>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                <p className="text-green-800">{submitMessage}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                <p className="text-red-800">{submitMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Nombre */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre Completo *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="name"
                    {...register('name')}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Tu nombre completo"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Empresa */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa (Opcional)
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="company"
                    {...register('company')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Nombre de tu empresa"
                  />
                </div>
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone')}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.phone ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Tu número de teléfono"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    {...register('email')}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="tu@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Asunto */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Asunto *
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    id="subject"
                    {...register('subject')}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.subject ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Selecciona un asunto</option>
                    <option value="cotizacion">Solicitar Cotización</option>
                    <option value="informacion_general">Información General</option>
                  </select>
                </div>
                {errors.subject && (
                  <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                )}
              </div>

              {/* Mensaje */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Mensaje *
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <textarea
                    id="message"
                    {...register('message')}
                    rows={5}
                    className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.message ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Cuéntanos sobre tu proyecto, especificaciones, cantidades, etc."
                  />
                </div>
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              {/* Botón de envío */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Enviar Mensaje
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Información de Contacto */}
          <div className="space-y-8">
            {/* Información de contacto */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Información de Contacto
              </h2>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                      {info.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {info.title}
                      </h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-gray-600 hover:text-primary-600 transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-gray-600">
                          {info.content}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mapa */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Nuestra Ubicación
              </h2>
              {mapEmbedUrl ? (
                <div className="rounded-lg overflow-hidden h-64 border border-gray-200">
                  <iframe
                    title="Mapa de ubicación de Plásticos Polcen"
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ) : (
                <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <MapPin className="h-16 w-16 mx-auto mb-4" />
                    <p>Mapa de ubicación</p>
                    <p className="text-sm mt-2">Santiago de Liniers 1239, Rivadavia, San Juan</p>
                  </div>
                </div>
              )}
              <div className="mt-4">
                <a
                  href={companyConfig.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary-700 hover:text-primary-800 font-medium"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Ver perfil en Google Maps
                </a>
              </div>
            </div>

            {/* Información adicional */}
            <div className="bg-primary-50 rounded-lg p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                ¿Por qué elegirnos?
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  desde Año 2013
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Calidad garantizada
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Asesoramiento técnico especializado
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Entrega rápida (7-15 días)
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Productos personalizados
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

